/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import apiClient from 'data/api/apiClient';
import { DetailedUserDto, AdminUsersListDto, AdminUsersListQueryParams, NewUserData } from './usersClient.typings';
import { cutEmptyParams, toHash } from '../../../utils/utils';
import { isOperatorRights, isTaxInspectorRights } from '../../../components/System/utils';

async function getAdminUsersList(params: AdminUsersListQueryParams): Promise<AdminUsersListDto> {
    return apiClient.get<AdminUsersListDto>('1.0.0/users', prepareAdminUsersListQueryParams(params));
}

async function getDetailedUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.get<DetailedUserDto>(`1.0.0/users/${userId}`);
}

async function updateDetailedUser(userId: string, data: Partial<NewUserData>): Promise<DetailedUserDto> {
    if (data.password) data.password = await toHash(data.password);
    return apiClient.patch<DetailedUserDto>(`1.0.0/users/${userId}`, data);
}

async function updateUserProfile(data: Partial<DetailedUserDto>): Promise<DetailedUserDto> {
    return apiClient.patch<DetailedUserDto>('1.0.0/users/profile', data);
}

async function changeUserPassword(userId: string, data: { oldPassword: string; newPassword: string }): Promise<void> {
    const hashedData = await Promise.all([await toHash(data.oldPassword), await toHash(data.newPassword)]);
    return apiClient.patch<void>(`1.0.0/users/${userId}/password`, {
        oldPassword: hashedData[0],
        newPassword: hashedData[1],
    });
}

async function blockUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.patch<DetailedUserDto>(`1.0.0/users/${userId}/block`);
}

async function unblockUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.patch<DetailedUserDto>(`1.0.0/users/${userId}/unblock`);
}

async function deleteUser(userId: string): Promise<DetailedUserDto> {
    return apiClient.delete<DetailedUserDto>(`1.0.0/users/${userId}/delete`);
}

async function addUser(data: Partial<NewUserData>): Promise<DetailedUserDto> {
    data.password = await toHash(data.password);

    return apiClient.post<DetailedUserDto>('1.0.0/users/add', data);
}

async function downloadAdminUsersListAsExcel(params: AdminUsersListQueryParams): Promise<ArrayBuffer> {
    return apiClient.downloadFile('1.0.0/users/excel', prepareAdminUsersListQueryParams(params));
}

const prepareAdminUsersListQueryParams = (params: AdminUsersListQueryParams): AdminUsersListQueryParams => {
    const { search_type, search_text } = params;
    delete params.search_text;
    delete params.search_type;

    if (!isTaxInspectorRights([...params.roles])) delete params.taxAuthorityId;
    if (!isOperatorRights([...params.roles])) delete params.operatorTaxNumber;

    const queryParams = { ...params, [search_type]: search_text };
    return cutEmptyParams(queryParams);
};

export const usersClient = {
    getAdminUsersList,
    getDetailedUser,
    updateUserProfile,
    updateDetailedUser,
    changeUserPassword,
    blockUser,
    unblockUser,
    deleteUser,
    addUser,
    downloadAdminUsersListAsExcel,
};
